import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type Column,
  type RowData,
  type Table,
  type TableFeature,
  type TableOptions_All,
} from '@tanstack/react-table';
import {
  column_getIsPinned,
  column_getIsVisible,
  column_getLeafColumns,
  table_getCenterVisibleLeafColumns,
  table_getStartVisibleLeafColumns,
  table_getEndVisibleLeafColumns,
  table_getVisibleLeafColumns,
} from '@tanstack/react-table/static-functions';
import { constructCell } from '@tanstack/table-core';

import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';

/**
 * Helper function to not duplicate this logic for the slightly different use cases of user actions and column settings modal.
 * @internal
 */
export function getCanHide<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  table: Table<TDataTableFeatures, TData>,
  column: Column<TDataTableFeatures, TData, TValue>,
  isHideable: (
    columns: Array<Column<TDataTableFeatures, TData, TValue>>,
  ) => boolean,
): boolean {
  // The feature is disabled altogether.
  if (
    (table.options as TableOptions_All<TDataTableFeatures, TData>)
      .enableHiding === false
  ) {
    return false;
  }

  // It is a leaf column, just look at the `enableHiding` for this column
  if (!column.columns || column.columns.length === 0) {
    return column.columnDef.enableHiding ?? true;
  }

  // It has sub-columns.
  // `enableHiding` will be false if it's explicitly set to false
  // Otherwise check the provided function
  return column.columnDef.enableHiding === false
    ? false
    : isHideable(column.columns);
}

/**
 * @returns true if *all* child columns can be hidden, false otherwise.
 */
function isParentColumnHideable<
  TData extends RowData,
  TValue extends CellData = CellData,
>(childColumns: Array<Column<TDataTableFeatures, TData, TValue>>): boolean {
  return childColumns.every((c) => c.columnDef.enableHiding);
}

export function table_getOrderedVisibleLeafColumns<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  return [
    ...table_getStartVisibleLeafColumns(table),
    ...table_getCenterVisibleLeafColumns(table),
    ...table_getEndVisibleLeafColumns(table),
  ];
}

/**
 * Helper function that provides an array of all visible leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getVisibleLeafColumnsWithoutBuiltin<
  TData extends RowData,
>(table: Table<TDataTableFeatures, TData>) {
  return table_getVisibleLeafColumns(table).filter(
    (leafColumn) => !leafColumn.columnDef.meta?.builtin,
  );
}

/**
 * Helper function that provides an array of all visible leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getAllLeafColumnsWithoutBuiltin<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  allLeafColumns: Array<Column<TDataTableFeatures, TData>>,
): Array<Column<TDataTableFeatures, TData>> {
  return allLeafColumns.filter((column) => !column.columnDef.meta?.builtin);
}

/**
 * Helper function that provides an array of all center visible leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getCenterVisibleLeafColumnsWithoutBuiltIn<
  TData extends RowData,
>(table: Table<TDataTableFeatures, TData>) {
  return table_getCenterVisibleLeafColumns(table).filter(
    (leafColumn) => !leafColumn.columnDef.meta?.builtin,
  );
}

/**
 * Helper function that provides the ids of all visible leaf-node columns that are currently rendered by the virtualizer, excluding builtin columns.
 * @internal
 */
export function table_getVisibleVirtualColumnIdsWithoutBuiltin<
  TData extends RowData,
>(table: Table<TDataTableFeatures, TData>) {
  const visibleLeafColumns = table_getVisibleLeafColumnsWithoutBuiltin(table);
  const virtualColumns = table.getColumnVirtualizer()?.getVirtualItems() ?? [];

  return virtualColumns.flatMap(
    (virtualItem) => visibleLeafColumns.at(virtualItem.index)?.id ?? [],
  );
}

/**
 * Helper function that provides an array of all visible leaf-node columns for a given column, excluding builtin columns.
 * @internal
 */
export function column_getIsLastVisibleColumn<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  // Find the last visible column, prioritizing right-pinned columns, then center columns
  const lastVisibleLeafColumnId =
    table_getEndVisibleLeafColumns(column.table).at(-1)?.id ??
    table_getCenterVisibleLeafColumns(column.table).at(-1)?.id;

  // return true if the current column or one of its child columns is the last visible column
  return (
    column.id === lastVisibleLeafColumnId ||
    column.columns.some(
      (childColumn) => childColumn.id === lastVisibleLeafColumnId,
    )
  );
}

/**
 * Helper function that provides an array of all visible leaf-node columns for a given column.
 * @internal
 */
export function column_getVisibleLeafColumns<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  return column_getLeafColumns(column).filter((leafColumn) =>
    column_getIsVisible(leafColumn),
  );
}

/**
 * Helper function that provides an array of all visible center leaf-node columns for a given column.
 * @internal
 */
export function column_getCenterVisibleLeafColumns<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  return column_getVisibleLeafColumns(column).filter(
    (leafColumn) => !column_getIsPinned(leafColumn),
  );
}

/**
 * Helper function that provides an array of all visible left leaf-node columns for a given column.
 * @internal
 */
export function column_getLeftVisibleLeafColumns<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  const ids = new Set(column_getVisibleLeafColumns(column).map((c) => c.id));
  return table_getStartVisibleLeafColumns(column.table).filter((c) =>
    ids.has(c.id),
  );
}
/**
 * Helper function that provides an array of all visible right leaf-node columns for a given column.
 * @internal
 */
export function column_getRightVisibleLeafColumns<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  const ids = new Set(column_getVisibleLeafColumns(column).map((c) => c.id));
  return table_getEndVisibleLeafColumns(column.table).filter((c) =>
    ids.has(c.id),
  );
}

/**
 * Helper function that provides whether a column can be hidden or not, based on the `enableHiding` property of the column definition and its parent columns.
 * @internal
 */
export function column_getCanHide<
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column<TDataTableFeatures, TData, TValue>) {
  return getCanHide(column.table, column, isParentColumnHideable);
}

/**
 * Constructs the column visibility feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructColumnVisiblityFeature(): TableFeature {
  return {
    assignColumnPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('columnVisibilityFeature', prototype, table, {
          column_getIsLastVisibleColumn: {
            fn: (column) => column_getIsLastVisibleColumn(column),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get(),
              table.options.columns,
            ],
          },
          column_getVisibleLeafColumns: {
            fn: (column) => column_getVisibleLeafColumns(column),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          column_getCenterVisibleLeafColumns: {
            fn: (column) => column_getCenterVisibleLeafColumns(column),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          column_getLeftVisibleLeafColumns: {
            fn: (column) => column_getLeftVisibleLeafColumns(column),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get()?.start,
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          column_getRightVisibleLeafColumns: {
            fn: (column) => column_getRightVisibleLeafColumns(column),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get()?.end,
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          column_getCanHide: {
            fn: (column) => column_getCanHide(column),
          },
        });
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('columnVisibilityFeature', table, {
          table_getAllLeafColumnsWithoutBuiltin: {
            fn: (leafColumns) =>
              table_getAllLeafColumnsWithoutBuiltin(table, leafColumns),
            memoDeps: () => [table.getAllLeafColumns()],
          },
          table_getOrderedVisibleLeafColumns: {
            fn: () => table_getOrderedVisibleLeafColumns(table),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          table_getVisibleLeafColumnsWithoutBuiltin: {
            fn: () => table_getVisibleLeafColumnsWithoutBuiltin(table),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          table_getCenterVisibleLeafColumnsWithoutBuiltIn: {
            fn: () => table_getCenterVisibleLeafColumnsWithoutBuiltIn(table),
            memoDeps: () => [
              table.atoms.columnVisibility?.get(),
              table.atoms.columnPinning?.get(),
              table.atoms.columnOrder?.get(),
              table.options.columns,
            ],
          },
          table_getVisibleVirtualColumnIdsWithoutBuiltin: {
            fn: () => table_getVisibleVirtualColumnIdsWithoutBuiltin(table),
          },
        });
      },
    ),
    assignRowPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs('columnVisibilityFeature', prototype, table, {
          // Overrides TanStack's `row_getVisibleCells`.
          row_getVisibleCells: {
            fn: (row) => row_getVisibleCells(row),
            // TanStack depends on `row.getAllCells()` here, which creates the
            // cells for every defined column too. These dependencies cover the
            // same changes without that cost.
            memoDeps: () => [
              table.getVisibleLeafColumns(),
              table.atoms.columnPinning?.get(),
            ],
          },
        });
      },
    ),
  };
}

type CellColumn = Parameters<typeof constructCell>[0];
type CellRow = Parameters<typeof constructCell>[1];
type TableCell = ReturnType<typeof constructCell>;

/**
 * TanStack keeps one cell per column on the row in `_cellsCache`. Reusing it
 * keeps cell identities stable, which memoized cell components rely on.
 */
type RowWithCellsCache = CellRow & {
  _cellsCache?: WeakMap<CellColumn, TableCell>;
};

/**
 * Replaces TanStack's `row_getVisibleCells`, which creates a cell for every
 * defined column — hidden ones included — and filters afterwards. Building
 * from the visible columns instead means columns that are defined but never
 * shown cost nothing per rendered row.
 * @internal
 */
export function row_getVisibleCells(row: RowWithCellsCache) {
  const table = row.table;
  row._cellsCache ??= new WeakMap<CellColumn, TableCell>();

  const cache = row._cellsCache;
  const visibleCells = table
    .getVisibleLeafColumns()
    .map((column: CellColumn) => {
      const cachedCell = cache.get(column);

      if (cachedCell) {
        return cachedCell;
      }

      const cell = constructCell(column, row, table);
      cache.set(column, cell);

      return cell;
    });

  const pinning = table.atoms.columnPinning?.get();
  const start = pinning?.start ?? [];
  const end = pinning?.end ?? [];

  if (start.length === 0 && end.length === 0) {
    return visibleCells;
  }

  // Pinned columns render at the edges, in the order they were pinned.
  const cellsByColumnId = new Map(
    visibleCells.map((cell): [string, TableCell] => [cell.column.id, cell]),
  );
  const isPinned = (cell: TableCell): boolean =>
    start.includes(cell.column.id) || end.includes(cell.column.id);

  return [
    ...start.flatMap((id: string) => cellsByColumnId.get(id) ?? []),
    ...visibleCells.filter((cell: TableCell) => !isPinned(cell)),
    ...end.flatMap((id: string) => cellsByColumnId.get(id) ?? []),
  ];
}

/**
 * Additional feature implementation for column Visiblity.
 * @internal
 */
export const DataTableColumnVisiblityFeature =
  constructColumnVisiblityFeature();
